import React, { useContext, useEffect, useState } from 'react';
import "./AdministrativeExpensesView.css";
import axios from "axios";
import AuthContext from "../../../contexts/AuthContext";
import { createColumnHelper } from "@tanstack/react-table";
import TanStackTable from "../../../components/Table/TanStackTable/TanStackTable";
import CellInfoModal from "../../../components/CellInfoModal/CellInfoModal";
import { currencyFormat } from "../../../utils/currencyFormat";
import { Badge, Flex, Group, NumberInput, Paper, Select, Skeleton, Stack, Table, Tabs, Text } from "@mantine/core";
import { DateInput, DatePickerInput } from "@mantine/dates";
import AppContext from "../../../contexts/AppContext";
import moment from "moment";
import { IconBook, IconCalendar, IconCoin, IconCurrencyDollar } from '@tabler/icons-react';


const findById = (_staffUsers, id) => {
    const sellerObj = _staffUsers.find((_user) => _user.value === id);
    return sellerObj ? sellerObj.label : id;
};

/**
 * Calculate payment details based on row data
 * Nueva estructura: cada installment es un documento separado con campos planos
 * @param row
 * @returns {{commission: string, installmentNumber: number|null, paymentType: string, currency: string}}
 */
function calculatePaymentDetails(row) {
    try {
        if (!row || !row.type) {
            throw new Error("Datos incompletos o inválidos en la fila");
        }

        const isInstallment = row.isInstallment;
        const paymentType = isInstallment ? "Cuota" : "Pago único";
        const installmentNumber = isInstallment ? row.installment_number : null;
        const commissionAmount = row.installment_commission ?? 0;
        const currency = row.installment_currency ?? "ARS";

        return {
            paymentType,
            commission: currencyFormat(commissionAmount),
            installmentNumber,
            currency
        };
    } catch (error) {
        console.error("Error al calcular detalles de pago:", error.message);
        return { paymentType: "Desconocido", commission: currencyFormat(0), installmentNumber: null, currency: "ARS" };
    }
}



const SimpleSalesDetailsTable = ({ data }) => {
    return (<Table.ScrollContainer minWidth={800}>
        <Table verticalSpacing="xs">
            <Table.Thead>
                <Table.Tr>
                    <Table.Th>Fecha</Table.Th>
                    <Table.Th>Tipo</Table.Th>
                    <Table.Th>Capacitación</Table.Th>
                    <Table.Th>Estudiante</Table.Th>
                    <Table.Th>Comisión</Table.Th>
                </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
                {data.map((row, index) => {
                    const { paymentType, commission, installmentNumber, currency } = calculatePaymentDetails(row);
                    const typeLabel = installmentNumber ? `${paymentType} ${installmentNumber}` : paymentType;
                    return (
                        <Table.Tr key={row._id || index}>
                            <Table.Td>{moment(new Date(row.installment_payment_date || row.created_at)).format("DD/MM/YYYY")}</Table.Td>
                            <Table.Td>{typeLabel}</Table.Td>
                            <Table.Td>{row.details?.[0]?.description ?? "-"}</Table.Td>
                            <Table.Td>{`${row.payer?.name ?? ""} ${row.payer?.lastname ?? ""}`}</Table.Td>
                            <Table.Td>{`$${commission} ${currency}`}</Table.Td>
                        </Table.Tr>
                    )
                })}
            </Table.Tbody>
        </Table>
    </Table.ScrollContainer>)
}

function AdministrativeExpensesView() {
    const { authToken } = useContext(AuthContext);
    const { staffUsers } = useContext(AppContext)
    const [expensesData, setExpensesData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [cellModal, setCellModal] = useState(null)

    // Get initDate as this month's first day
    const firstDayOfThisMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    const currentDay = new Date();
    const [dateFilter, setDateFilter] = useState([firstDayOfThisMonth, currentDay]);

    // COLUMN DEFINITIONS
    const columnHelper = createColumnHelper();
    const columns = [
        columnHelper.accessor("name", {
            cell: (info) => info.getValue(),
            header: "Nombre",
            enableColumnFilter: true,
            enableSorting: false,
            filter: "text",
            filterFn: "includesString",
        }),

        /*   {
    _id: new ObjectId("66fd6ce03ebcd6be198b260d"),
    name: 'Gonzalo  Godoy',
    teacherInfo: {},
    lessonsData: [ [Object], [Object], [Object] ],
    totals: { totalARS: 0, totalUSD: 300 }
  }, */
        columnHelper.accessor("totals.totalUSD", {
            cell: (info) => {
                return <span className="AdministrativeExpensesView__cell--center">
                    {info.getValue()}
                </span>
            },
            header: "Total USD",
            enableColumnFilter: false,
            enableSorting: true,
            size: 88,
        }),
        columnHelper.accessor("totals.totalARS", {
            cell: (info) => {
                return <span className="AdministrativeExpensesView__cell--center">
                    {info.getValue()}
                </span>
            },
            header: "Total ARS",
            enableColumnFilter: false,
            enableSorting: true,
            size: 88,
        }),
        columnHelper.accessor("lessonsData", {
            cell: (info) =>
                <span className="AdministrativeExpensesView__cell--center">
                    {info.getValue().length}
                </span>,
            header: "Clases",
            enableColumnFilter: false,
            enableSorting: true,
            size: 88,
        }),
        columnHelper.accessor("lessonsData", {
            cell: (info) => <span
                className="AdministrativeExpensesView__cell"
                onClick={() => setCellModal({
                    text: <Stack gap="xl" py="md">{info.getValue().map((_l, index) => <Paper key={_l.lesson_id} withBorder p="md" radius="md" bg="var(--mantine-color-gray-0)">

                        <Stack gap='sm'>
                            <Group justify="space-between">
                                <Group gap="xs">
                                    <IconBook size={16} />
                                    <Text fw={500} size="sm">{_l.name}</Text>
                                </Group>
                                <Badge color="gray" variant="outline">Clase #{index + 1}</Badge>
                            </Group>
                            <Flex
                                gap="md"
                                key={_l.lesson_id}
                                direction={{ base: 'column', sm: 'row' }}
                                align={{ sm: 'flex-end' }}
                            >

                                <DateInput
                                    label="Fecha de inicio"
                                    leftSection={<IconCalendar size={16} />}
                                    value={_l.date ? new Date(_l.date) : null}
                                    style={{ flex: 1 }}
                                    readOnly
                                />

                                <NumberInput
                                    label="Monto de tarifa"
                                    placeholder="0.00"
                                    hideControls
                                    prefix="$ "
                                    min={0}
                                    leftSection={<IconCurrencyDollar size={16} />}
                                    style={{ flex: 1 }}
                                    value={_l.fee.amount}
                                    readOnly

                                />

                                <Select
                                    label="Moneda"
                                    data={['USD', 'ARS']}
                                    leftSection={<IconCoin size={16} />}
                                    value={_l.fee.currency}
                                    style={{ flex: 0.5 }}
                                    readOnly

                                />
                            </Flex>
                        </Stack>
                    </Paper>
                    )}</Stack>,
                    title: 'Clases dictadas'
                })}
            >{info.getValue().map(_l => _l.name).join(",")}</span>,
            header: "Detalle",
            enableColumnFilter: false,
            enableSorting: true,
        }),
    ];
    const modColumns = [
        columnHelper.accessor("name", {
            cell: (info) => info.getValue(),
            header: "Nombre",
            enableColumnFilter: true,
            enableSorting: false,
            filter: "text",
            filterFn: "includesString",
        }),
        columnHelper.accessor("", {
            cell: (info) => {
                const { staffInfo, lessonsData } = info.row.original;
                if (!staffInfo.lessonFee) return null;
                return (
                    <span className="AdministrativeExpensesView__cell--center">
                        {currencyFormat(staffInfo.lessonFee * lessonsData.length)}
                    </span>);
            },
            header: "Total",
            enableColumnFilter: false,
            enableSorting: true,
            size: 88,
        }),
        columnHelper.accessor("staffInfo.feeCurrency", {
            cell: (info) =>
                <span className="AdministrativeExpensesView__cell--center">
                    {info.getValue()}
                </span>,
            header: "Moneda",
            enableColumnFilter: true,
            enableSorting: false,
            filter: "select",
            filterOptions: [
                { value: 'ARS', label: 'ARS' },
                { value: 'USD', label: 'USD' },
            ],
            size: 88,
            filterFn: (row, id, filterValue) => {
                if (!filterValue) return true;
                return row.original.staffInfo.feeCurrency.toLowerCase() === filterValue.toLowerCase();
            }
        }),
        columnHelper.accessor("staffInfo", {
            cell: (info) => {
                if (!info.getValue().lessonFee) return null;
                return <span className="AdministrativeExpensesView__cell--center">
                    {info.getValue().lessonFee} {info.getValue().feeCurrency}
                </span>
            },
            header: "Valor clase",
            enableColumnFilter: false,
            enableSorting: true,
            size: 104,
        }),
        columnHelper.accessor("lessonsData", {
            cell: (info) =>
                <span className="AdministrativeExpensesView__cell--center">
                    {info.getValue().length}
                </span>,
            header: "Clases",
            enableColumnFilter: false,
            enableSorting: true,
            size: 88,
        }),
        columnHelper.accessor("lessonsData", {
            cell: (info) => <span
                className="AdministrativeExpensesView__cell"
                onClick={() => setCellModal({
                    text: <ul>{info.getValue().map(_l => <li className="AdministrativeExpensesView__ul">{_l.name}</li>)}</ul>,
                    title: 'Clases dictadas'
                })}
            >{info.getValue().map(_l => _l.name).join(",")}</span>,
            header: "Detalle",
            enableColumnFilter: false,
            enableSorting: true,
        }),
    ];
    const salesColumns = [
        columnHelper.accessor("sellerId", {
            cell: (info) => {
                const sellerId = info.getValue();
                return findById(staffUsers, sellerId);
            },
            header: "Vendedor",
            enableColumnFilter: false,
            enableSorting: true
        }),
        columnHelper.accessor("commissions.totalARS", {
            cell: (info) => {
                return `$ ${currencyFormat(info.getValue())}`;
            },
            header: "Comisiones ARS",
            enableColumnFilter: false,
            enableSorting: true,
            size: 120,
        }),
        columnHelper.accessor("commissions.totalUSD", {
            cell: (info) => {
                return `$ ${currencyFormat(info.getValue())}`;
            },
            header: "Comisiones USD",
            enableColumnFilter: false,
            enableSorting: true,
            size: 120,
        }),
        columnHelper.accessor("sales", {
            cell: (info) =>
                <span
                    className="AdministrativeExpensesView__cell--center"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setCellModal({
                        // Or use TanStack instead
                        text: <SimpleSalesDetailsTable
                            data={info.row.original.sales}
                        />,
                        title: `Detalle de ventas de ${findById(staffUsers, info.row.original.sellerId)}`,
                        darkMode: false
                    })}
                >
                    Ver detalles
                </span>,
            header: "Detalle",
            enableColumnFilter: false,
            enableSorting: true,
            size: 88
        })
    ];

    // DATA INITIALIZATION
    const init = async () => {
        setLoading(true);
        const url = `${process.env.REACT_APP_BACKEND_SERVER}admin/administracion/getExpenses?initDate=${dateFilter[0]}&endDate=${dateFilter[1]}`;
        const config = {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${authToken}`,
            },
        };
        const expensesResponse = await axios.get(url, config);

        // Sort data by lessonsData length
        expensesResponse.data.teachers.sort((a, b) => b.lessonsData.length - a.lessonsData.length);
        expensesResponse.data.moderators.sort((a, b) => b.lessonsData.length - a.lessonsData.length);

        setExpensesData(expensesResponse.data);
        setLoading(false);
    };

    useEffect(() => {
        if (dateFilter[0] && dateFilter[1])
            init();
    }, [dateFilter]);


    return (
        <div className="AdministrativeExpensesView__container">
            <div className="AdministrativeExpensesView__filters">
                <DatePickerInput
                    classNames={{ root: "moderationContainer-DatePicker-root" }}
                    type="range"
                    label="Mostrando datos de:"
                    value={dateFilter}
                    onChange={(value) => setDateFilter(value)}
                />
                <span>
                    * Por defecto se muestran datos del mes actual (desde el 1ro hasta la fecha actual)
                </span>
            </div>

            {loading && < Skeleton h='400px' w='100%' />}
            {(!loading) && <Tabs
                defaultValue="Profesores"
                style={{ width: "100%" }}
            >
                <Tabs.List>
                    <Tabs.Tab value="Profesores">
                        Profesores
                    </Tabs.Tab>
                    <Tabs.Tab value="Moderadores">
                        Moderadores
                    </Tabs.Tab>
                    <Tabs.Tab value="Vendedores">
                        Vendedores
                    </Tabs.Tab>
                </Tabs.List>

                {expensesData?.teachers &&
                    <Tabs.Panel value="Profesores" pt={20}>
                        <TanStackTable
                            loading={loading}
                            data={expensesData.teachers}
                            columns={columns}
                        />
                    </Tabs.Panel>}

                {expensesData?.moderators &&
                    <Tabs.Panel value="Moderadores" pt={20}>
                        <TanStackTable
                            loading={loading}
                            data={expensesData.moderators}
                            columns={modColumns}
                        />
                    </Tabs.Panel>
                }
                {expensesData?.sales &&
                    <Tabs.Panel value="Vendedores" pt={20}>
                        <TanStackTable
                            loading={loading}
                            data={expensesData.sales}
                            columns={salesColumns}
                        />
                    </Tabs.Panel>
                }
            </Tabs>}


            <CellInfoModal opened={cellModal} onCloseFunct={setCellModal} text={cellModal?.text}
                title={cellModal?.title} darkMode={cellModal?.darkMode} />
        </div>
    );
}

export default AdministrativeExpensesView;